import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Search, CircleCheck, CircleX } from "lucide-react";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";

const OUTCOME_TONE = {
  allowed: "bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)]",
  denied: "bg-[color:var(--overdue-bg)] text-[color:var(--overdue)]",
};

function OutcomePill({ outcome }) {
  const Icon = outcome === "allowed" ? CircleCheck : CircleX;
  const cls = OUTCOME_TONE[outcome] || "bg-gray-100 text-gray-600";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-semibold ${cls}`}
      data-testid={`login-outcome-${outcome}`}
    >
      <Icon className="w-3 h-3" /> {outcome === "allowed" ? "Allowed" : "Denied"}
    </span>
  );
}

function AuditTabs() {
  const loc = useLocation();
  const onPayments = loc.pathname === "/audit";
  const tab = (to, label, active) => (
    <Link
      key={to}
      to={to}
      data-testid={`audit-tab-${label.toLowerCase()}`}
      className={
        "px-3 py-1.5 text-[13px] rounded-sm transition-colors " +
        (active
          ? "bg-[color:var(--aisensy-dark)] text-white font-semibold"
          : "text-ink-2 hover:text-ink hover:bg-[color:var(--bone)]")
      }
    >
      {label}
    </Link>
  );
  return (
    <div className="inline-flex bg-card border border-line rounded-sm p-0.5">
      {tab("/audit", "Payments", onPayments)}
      {tab("/audit/logins", "Sign-ins", !onPayments)}
    </div>
  );
}

export { AuditTabs };

export default function LoginAuditPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("all");
  const [method, setMethod] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (outcome !== "all") params.outcome = outcome;
      if (method !== "all") params.method = method;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/audit/logins", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [outcome, method, q]);

  useEffect(() => {
    load();
  }, [load]);

  const pageRows = pageSize === "All" || pageSize === 0
    ? rows
    : rows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);

  const allowedCount = rows.filter((r) => r.outcome === "allowed").length;
  const deniedCount = rows.filter((r) => r.outcome === "denied").length;

  return (
    <div className="space-y-4" data-testid="login-audit-page">
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="eyebrow">Reconciliation</div>
        <h1 className="font-display text-2xl leading-none">Access Log</h1>
      </div>
      <div className="text-sm text-ink-2 flex items-center justify-between gap-3 flex-wrap">
        <span>
          Every sign-in attempt — Google <b>and</b> email/password, allowed and
          denied. Watch for removed team members or unknown emails trying to
          get in.
        </span>
        <AuditTabs />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[color:var(--line)] border border-line rounded-sm overflow-hidden">
        <div className="bg-card p-4">
          <div className="eyebrow">Attempts in view</div>
          <div className="text-2xl font-semibold tabular mt-1">{total}</div>
        </div>
        <div className="bg-card p-4">
          <div className="eyebrow">Allowed</div>
          <div className="text-2xl font-semibold tabular mt-1 text-[color:var(--aisensy-dark)]">
            {allowedCount}
          </div>
        </div>
        <div className="bg-card p-4">
          <div className="eyebrow">Denied</div>
          <div className="text-2xl font-semibold tabular mt-1 text-[color:var(--overdue)]">
            {deniedCount}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-ink-2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email…"
            className="pl-8 h-9 w-64"
            data-testid="login-audit-search"
          />
        </div>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="h-9 w-40" data-testid="login-audit-outcome">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="allowed">Allowed</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
          </SelectContent>
        </Select>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="h-9 w-40" data-testid="login-audit-method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All methods</SelectItem>
            <SelectItem value="google">Google</SelectItem>
            <SelectItem value="password">Password</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-line rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--bone)] text-ink-2">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Method</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Reason / IP</th>
            </tr>
          </thead>
          <tbody className="[&>tr]:border-t [&>tr]:border-line">
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-ink-2">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-ink-2">
                  No sign-in attempts logged yet.
                </td>
              </tr>
            )}
            {pageRows.map((r) => {
              const ts = r.timestamp
                ? new Date(r.timestamp).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })
                : "—";
              const methodLabel = r.method === "password" ? "Password" : "Google";
              return (
                <tr key={r.id} data-testid={`login-audit-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-ink-2 tabular">{ts}</td>
                  <td className="px-3 py-2 font-medium">{r.email || "—"}</td>
                  <td className="px-3 py-2 text-ink-2">{r.name || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 bg-[color:var(--bone)] border border-line rounded-sm text-[11px] font-semibold text-ink"
                      data-testid={`login-method-${r.method || "google"}`}
                    >
                      {methodLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-ink-2">{r.role || "—"}</td>
                  <td className="px-3 py-2"><OutcomePill outcome={r.outcome} /></td>
                  <td className="px-3 py-2 text-ink-2">
                    {r.reason && <div>{r.reason}</div>}
                    {r.ip && <div className="text-[11px] text-ink-2/70 tabular">{r.ip}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        total={rows.length}
      />
    </div>
  );
}
